'use client';

import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import { Field, Input, SettingsEditDialog } from '@terra/ui-design-system';

type DiscoveryContentTypeAddDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mimeType: string;
  description: string;
  error: string | null;
  disabled?: boolean;
  onMimeTypeChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onSubmit: () => void;
};

export function DiscoveryContentTypeAddDialog({
  open,
  onOpenChange,
  mimeType,
  description,
  error,
  disabled,
  onMimeTypeChange,
  onDescriptionChange,
  onSubmit,
}: DiscoveryContentTypeAddDialogProps) {
  const t = useTranslations('settings.discoveryContentTypes');
  const tCommon = useTranslations('common');
  const submitOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <SettingsEditDialog
      open={open}
      title={t('addDialogTitle')}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      submitText={tCommon('add')}
      cancelText={tCommon('cancel')}
      isSubmitDisabled={disabled}
      contentMaxW="md"
    >
      <VStack gap="4" alignItems="stretch" w="full">
        <Field.Root invalid={Boolean(error)}>
          <Field.Label>
            <Field.Mandatory /> {t('mimeTypeLabel')}
          </Field.Label>
          <Input
            placeholder={t('mimeTypePlaceholder')}
            value={mimeType}
            disabled={disabled}
            onChange={(event) => onMimeTypeChange(event.target.value)}
            onKeyDown={submitOnEnter}
          />
          {error ? <Field.ErrorText>{error}</Field.ErrorText> : null}
        </Field.Root>

        <Field.Root>
          <Field.Label>{tCommon('description')}</Field.Label>
          <Input
            placeholder={t('descriptionPlaceholder')}
            value={description}
            disabled={disabled}
            onChange={(event) => onDescriptionChange(event.target.value)}
            onKeyDown={submitOnEnter}
          />
        </Field.Root>
      </VStack>
    </SettingsEditDialog>
  );
}
